import { useEffect, useState } from 'react';
import { get } from 'lodash';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Divider,
  IconButton,
  Snackbar,
  Stack,
  TextField,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import { Save, Add, Delete, ExpandMore, Edit, Check, Close } from '@mui/icons-material';
import { Icon } from '@iconify/react';
import Page from '../../components/Page';
import { getInstitutionConfig, updateInstitutionConfig, clearRedisCache } from '../../axiosLib/services/institutionConfig/admin';
import { listAllSubjectsWithChapters, createSubject } from '../../axiosLib/services/subject/admin';
import { createChapter, deleteChapter } from '../../axiosLib/services/chapter/admin';

const SUBJECT_ICONS = [
  'mdi:dna', 'mdi:atom', 'mdi:flask-round-bottom', 'mdi:book-open-variant', 'mdi:earth',
  'mdi:calculator', 'mdi:brain', 'mdi:school', 'mdi:microscope', 'mdi:leaf',
  'mdi:music', 'mdi:palette', 'mdi:code-braces', 'mdi:chart-line', 'mdi:translate',
  'mdi:history', 'mdi:gavel', 'mdi:hospital-building', 'mdi:pill', 'mdi:stethoscope',
];
const SUBJECT_COLORS = [
  '#16a34a', '#2563eb', '#dc2626', '#9333ea', '#ea580c',
  '#0891b2', '#d946ef', '#4338ca', '#0d9488', '#ca8a04',
];

export default function InstitutionConfig() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [subjects, setSubjects] = useState([]);
  const [newSubjectName, setNewSubjectName] = useState('');
  const [newSubjectCode, setNewSubjectCode] = useState('');
  const [newChapterInputs, setNewChapterInputs] = useState({});
  const [subjectsLoading, setSubjectsLoading] = useState(false);
  const [newSubjectIcon, setNewSubjectIcon] = useState('mdi:book-open-variant');
  const [newSubjectColor, setNewSubjectColor] = useState('#4338ca');
  const [availableRoles, setAvailableRoles] = useState([]);
  const [clearingCache, setClearingCache] = useState(false);

  useEffect(() => {
    loadData();
    loadSubjects();
  }, []);

  const loadSubjects = () => {
    setSubjectsLoading(true);
    listAllSubjectsWithChapters((err, data) => {
      setSubjectsLoading(false);
      if (!err && Array.isArray(data)) setSubjects(data);
    });
  };

  const handleAddSubject = () => {
    if (!newSubjectName.trim() || !newSubjectCode.trim()) return;
    createSubject({ name: newSubjectName.trim(), subjectCode: newSubjectCode.trim(), icon: newSubjectIcon, color: newSubjectColor }, (err) => {
      if (!err) {
        setNewSubjectName('');
        setNewSubjectCode('');
        setNewSubjectIcon('mdi:book-open-variant');
        setNewSubjectColor('#4338ca');
        setSnackbar({ open: true, message: 'Subject added!', severity: 'success' });
        loadSubjects();
      } else {
        setSnackbar({ open: true, message: 'Failed to add subject', severity: 'error' });
      }
    });
  };

  const handleAddChapter = (subjectId) => {
    const name = (newChapterInputs[subjectId] || '').trim();
    if (!name) return;
    createChapter({ chapterName: name, subjectId }, (err) => {
      if (!err) {
        setNewChapterInputs({ ...newChapterInputs, [subjectId]: '' });
        loadSubjects();
      } else {
        setSnackbar({ open: true, message: 'Failed to add chapter', severity: 'error' });
      }
    });
  };

  const handleDeleteChapter = (chapterId) => {
    deleteChapter({ chapterId }, (err) => {
      if (!err) loadSubjects();
      else setSnackbar({ open: true, message: 'Failed to delete chapter', severity: 'error' });
    });
  };

  const handleUpdateSubjectStyle = (subjectId, field, value) => {
    const subj = subjects.find(s => s._id === subjectId);
    if (!subj) return;
    createSubject({
      _id: subjectId,
      name: subj.name,
      subjectCode: subj.subjectCode,
      icon: field === 'icon' ? value : subj.icon,
      color: field === 'color' ? value : subj.color,
    }, (err) => {
      if (!err) loadSubjects();
    });
  };

  const loadData = () => {
    setLoading(true);
    getInstitutionConfig((err, result) => {
      setLoading(false);
      if (!err) {
        setData(get(result, 'institution', null));
        setAvailableRoles(get(result, 'availableRoles', []));
      } else {
        setSnackbar({ open: true, message: 'Failed to load institution data', severity: 'error' });
      }
    });
  };

  const handleChange = (field, value) => {
    setData({ ...data, [field]: value });
  };

  // SMS Numbers management
  const handleSmsNumberChange = (index, value) => {
    const arr = [...(data.smsNumbers || [])];
    arr[index] = value;
    setData({ ...data, smsNumbers: arr });
  };

  const handleAddSmsNumber = () => {
    setData({ ...data, smsNumbers: [...(data.smsNumbers || []), ''] });
  };

  const handleRemoveSmsNumber = (index) => {
    const arr = [...(data.smsNumbers || [])];
    arr.splice(index, 1);
    setData({ ...data, smsNumbers: arr });
  };

  // Sessions management
  const handleSessionChange = (index, value) => {
    const arr = [...(data.sessions || [])];
    arr[index] = value;
    setData({ ...data, sessions: arr });
  };

  const handleAddSession = () => {
    setData({ ...data, sessions: [...(data.sessions || []), ''] });
  };

  const handleRemoveSession = (index) => {
    const arr = [...(data.sessions || [])];
    arr.splice(index, 1);
    setData({ ...data, sessions: arr });
  };

  const handleSave = () => {
    if (!data) return;
    setSaving(true);

    const payload = {
      institutionName: data.institutionName,
      mobile: data.mobile,
      email: data.email,
      address: data.address,
      smsNumbers: data.smsNumbers,
      sessions: data.sessions,
      vendor: data.vendor,
      teachers: data.teachers,
      smsLimit: data.smsLimit,
      remainSms: data.remainSms,
      agencyWideRoles: data.agencyWideRoles,
    };

    updateInstitutionConfig(payload, (err, result) => {
      setSaving(false);
      if (!err) {
        setSnackbar({ open: true, message: 'Saved successfully!', severity: 'success' });
        setData(get(result, 'institution', data));
      } else {
        setSnackbar({ open: true, message: 'Save failed', severity: 'error' });
      }
    });
  };

  return (
    <Page title="Institution Config">
      <Container maxWidth="md">
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
          <Typography variant="h4" gutterBottom>
            Institution Config
          </Typography>
          {data && (
            <Button
              variant="contained"
              startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <Save />}
              onClick={handleSave}
              disabled={saving}
              sx={{
                background: 'linear-gradient(45deg, #00d4ff, #7b2ff7)',
                fontWeight: 'bold',
                px: 4,
                py: 1.5,
                '&:hover': { background: 'linear-gradient(45deg, #00b8d4, #6a1bd4)' },
              }}
            >
              {saving ? 'Saving...' : 'SAVE'}
            </Button>
          )}
        </Stack>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
            <CircularProgress />
          </Box>
        ) : !data ? (
          <Typography>No institution data found.</Typography>
        ) : (
          <Stack spacing={3}>
            {/* Basic Info */}
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Basic Information
                </Typography>
                <Stack spacing={2}>
                  <TextField
                    label="Institution Name"
                    value={get(data, 'institutionName', '')}
                    onChange={(e) => handleChange('institutionName', e.target.value)}
                    fullWidth
                  />
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <TextField
                      label="Mobile"
                      value={get(data, 'mobile', '')}
                      onChange={(e) => handleChange('mobile', e.target.value)}
                      fullWidth
                    />
                    <TextField
                      label="Email"
                      value={get(data, 'email', '')}
                      onChange={(e) => handleChange('email', e.target.value)}
                      fullWidth
                    />
                  </Stack>
                  <TextField
                    label="Address"
                    value={get(data, 'address', '')}
                    onChange={(e) => handleChange('address', e.target.value)}
                    fullWidth
                    multiline
                    rows={2}
                  />
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <TextField
                      label="Vendor"
                      value={get(data, 'vendor', '')}
                      onChange={(e) => handleChange('vendor', e.target.value)}
                      fullWidth
                    />
                    <TextField
                      label="Teachers"
                      value={get(data, 'teachers', '')}
                      onChange={(e) => handleChange('teachers', e.target.value)}
                      fullWidth
                    />
                  </Stack>
                </Stack>
              </CardContent>
            </Card>

            {/* SMS Config */}
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  SMS Configuration
                </Typography>
                <Stack spacing={2}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <TextField
                      label="SMS Limit"
                      type="number"
                      value={get(data, 'smsLimit', 0)}
                      onChange={(e) => handleChange('smsLimit', Number(e.target.value))}
                      fullWidth
                    />
                    <TextField
                      label="Remaining SMS"
                      type="number"
                      value={get(data, 'remainSms', 0)}
                      onChange={(e) => handleChange('remainSms', Number(e.target.value))}
                      fullWidth
                    />
                    <TextField
                      label="Used SMS"
                      value={get(data, 'usedSms', 0)}
                      disabled
                      fullWidth
                    />
                  </Stack>

                  <Divider />

                  <Typography variant="subtitle2" color="text.secondary">
                    SMS Numbers
                  </Typography>
                  {(data.smsNumbers || []).map((num, idx) => (
                    <Stack key={idx} direction="row" spacing={1} alignItems="center">
                      <TextField
                        size="small"
                        value={num}
                        onChange={(e) => handleSmsNumberChange(idx, e.target.value)}
                        placeholder="Phone number"
                        fullWidth
                      />
                      <IconButton size="small" onClick={() => handleRemoveSmsNumber(idx)} sx={{ color: '#ff4444' }}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </Stack>
                  ))}
                  <Button size="small" variant="outlined" startIcon={<Add />} onClick={handleAddSmsNumber}>
                    Add SMS Number
                  </Button>
                </Stack>
              </CardContent>
            </Card>

            {/* Sessions */}
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Sessions
                </Typography>
                <Stack spacing={1}>
                  <Stack direction="row" flexWrap="wrap" gap={1}>
                    {(data.sessions || []).map((session, idx) => (
                      <Chip
                        key={idx}
                        label={session}
                        onDelete={() => handleRemoveSession(idx)}
                        color="primary"
                        variant="outlined"
                      />
                    ))}
                  </Stack>
                  <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                    <TextField
                      size="small"
                      placeholder="New session (e.g. 2027)"
                      id="new-session-input"
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          const val = e.target.value.trim();
                          if (val) {
                            setData({ ...data, sessions: [...(data.sessions || []), val] });
                            e.target.value = '';
                          }
                        }
                      }}
                    />
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<Add />}
                      onClick={() => {
                        const input = document.getElementById('new-session-input');
                        const val = input.value.trim();
                        if (val) {
                          setData({ ...data, sessions: [...(data.sessions || []), val] });
                          input.value = '';
                        }
                      }}
                    >
                      Add
                    </Button>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
            {/* Agency Wide Roles */}
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Agency Wide Roles
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Toggle roles to enable/disable features for this institution.
                </Typography>
                <Stack direction="row" flexWrap="wrap" gap={1}>
                  {availableRoles.map((role) => {
                    const isActive = (data.agencyWideRoles || []).includes(role);
                    return (
                      <Chip
                        key={role}
                        label={role.replace(/_/g, ' ')}
                        icon={isActive ? <Check /> : <Add />}
                        onClick={() => {
                          const current = [...(data.agencyWideRoles || [])];
                          if (isActive) {
                            setData({ ...data, agencyWideRoles: current.filter(r => r !== role) });
                          } else {
                            setData({ ...data, agencyWideRoles: [...current, role] });
                          }
                        }}
                        color={isActive ? 'secondary' : 'default'}
                        variant={isActive ? 'filled' : 'outlined'}
                        sx={{
                          fontWeight: isActive ? 700 : 400,
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                        }}
                      />
                    );
                  })}
                </Stack>
              </CardContent>
            </Card>

            {/* Subjects & Chapters */}
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  📚 Subjects & Chapters
                </Typography>

                {subjectsLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                    <CircularProgress size={28} />
                  </Box>
                ) : (
                  <Stack spacing={1}>
                    {subjects.map((subj) => (
                      <Accordion key={subj._id} sx={{ '&:before': { display: 'none' }, borderRadius: '8px !important', border: `1px solid ${(subj.color || '#4338ca')}22` }}>
                        <AccordionSummary expandIcon={<ExpandMore />}>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Box sx={{
                              width: 32, height: 32, borderRadius: 1.5, display: 'flex',
                              alignItems: 'center', justifyContent: 'center',
                              background: `${subj.color || '#4338ca'}15`,
                            }}>
                              <Icon icon={subj.icon || 'mdi:book-open-variant'}
                                style={{ fontSize: 18, color: subj.color || '#4338ca' }} />
                            </Box>
                            <Typography sx={{ fontWeight: 700 }}>{subj.name}</Typography>
                            <Chip size="small" label={subj.subjectCode} sx={{ fontSize: '0.7rem' }} />
                            <Chip size="small" label={`${(subj.chapters || []).length} chapters`}
                              sx={{ fontSize: '0.7rem', background: '#ecfdf5', color: '#059669' }} />
                          </Stack>
                        </AccordionSummary>
                        <AccordionDetails>
                          <Stack spacing={0.5}>
                            {(subj.chapters || []).map((ch) => (
                              <Stack key={ch._id} direction="row" alignItems="center" spacing={1}
                                sx={{ py: 0.5, px: 1, borderRadius: 1, '&:hover': { background: '#f9fafb' } }}>
                                <Typography sx={{ flex: 1, fontSize: '0.85rem', color: '#444' }}>
                                  • {ch.chapterName}
                                </Typography>
                                <IconButton size="small" onClick={() => handleDeleteChapter(ch._id)}
                                  sx={{ color: '#ef4444', opacity: 0.6, '&:hover': { opacity: 1 } }}>
                                  <Delete fontSize="small" />
                                </IconButton>
                              </Stack>
                            ))}
                            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                              <TextField size="small" placeholder="New chapter name" fullWidth
                                value={newChapterInputs[subj._id] || ''}
                                onChange={(e) => setNewChapterInputs({ ...newChapterInputs, [subj._id]: e.target.value })}
                                onKeyPress={(e) => { if (e.key === 'Enter') handleAddChapter(subj._id); }}
                              />
                              <Button size="small" variant="outlined" startIcon={<Add />}
                                onClick={() => handleAddChapter(subj._id)}
                                sx={{ whiteSpace: 'nowrap' }}>
                                Add
                              </Button>
                            </Stack>
                          </Stack>

                          <Divider sx={{ my: 1.5 }} />
                          <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#888', mb: 0.5 }}>Icon & Color</Typography>
                          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
                            {SUBJECT_ICONS.map(ic => (
                              <IconButton key={ic} size="small"
                                onClick={() => handleUpdateSubjectStyle(subj._id, 'icon', ic)}
                                sx={{
                                  border: (subj.icon || 'mdi:book-open-variant') === ic ? `2px solid ${subj.color || '#4338ca'}` : '1px solid #e5e7eb',
                                  borderRadius: 1, p: 0.5,
                                  background: (subj.icon || 'mdi:book-open-variant') === ic ? `${subj.color || '#4338ca'}10` : 'transparent',
                                }}>
                                <Icon icon={ic} style={{ fontSize: 16, color: (subj.icon || 'mdi:book-open-variant') === ic ? (subj.color || '#4338ca') : '#888' }} />
                              </IconButton>
                            ))}
                          </Stack>
                          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                            {SUBJECT_COLORS.map(c => (
                              <Box key={c} onClick={() => handleUpdateSubjectStyle(subj._id, 'color', c)}
                                sx={{
                                  width: 24, height: 24, borderRadius: 1, background: c, cursor: 'pointer',
                                  border: (subj.color || '#4338ca') === c ? '3px solid #333' : '1px solid #ddd',
                                  transition: 'all 0.15s',
                                  '&:hover': { transform: 'scale(1.15)' },
                                }} />
                            ))}
                          </Stack>
                        </AccordionDetails>
                      </Accordion>
                    ))}

                    <Divider sx={{ my: 1 }} />
                    <Typography variant="subtitle2" color="text.secondary">Add New Subject</Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Stack direction="row" spacing={0.3}>
                        {SUBJECT_ICONS.slice(0, 6).map(ic => (
                          <IconButton key={ic} size="small" onClick={() => setNewSubjectIcon(ic)}
                            sx={{
                              border: newSubjectIcon === ic ? `2px solid ${newSubjectColor}` : '1px solid #e5e7eb',
                              borderRadius: 1, p: 0.3,
                            }}>
                            <Icon icon={ic} style={{ fontSize: 14, color: newSubjectIcon === ic ? newSubjectColor : '#aaa' }} />
                          </IconButton>
                        ))}
                      </Stack>
                      <Stack direction="row" spacing={0.3}>
                        {SUBJECT_COLORS.slice(0, 5).map(c => (
                          <Box key={c} onClick={() => setNewSubjectColor(c)}
                            sx={{
                              width: 20, height: 20, borderRadius: 0.5, background: c, cursor: 'pointer',
                              border: newSubjectColor === c ? '2px solid #333' : '1px solid #ddd',
                            }} />
                        ))}
                      </Stack>
                    </Stack>
                    <Stack direction="row" spacing={1}>
                      <TextField size="small" placeholder="Subject Name" value={newSubjectName}
                        onChange={(e) => setNewSubjectName(e.target.value)} sx={{ flex: 2 }} />
                      <TextField size="small" placeholder="Code (e.g. BIO)" value={newSubjectCode}
                        onChange={(e) => setNewSubjectCode(e.target.value)} sx={{ flex: 1 }} />
                      <Button variant="contained" startIcon={<Add />} onClick={handleAddSubject}
                        sx={{ background: 'linear-gradient(45deg, #00d4ff, #7b2ff7)', whiteSpace: 'nowrap' }}>
                        Add
                      </Button>
                    </Stack>
                  </Stack>
                )}
              </CardContent>
            </Card>

            {/* Bottom save */}
            {/* ═══════ DANGER ZONE ═══════ */}
            <Card sx={{
              borderRadius: 3, border: '1px solid rgba(239,68,68,0.3)',
              background: 'rgba(239,68,68,0.03)',
            }}>
              <CardContent sx={{ p: 3 }}>
                <Typography sx={{ fontWeight: 800, color: '#dc2626', fontSize: '1rem', mb: 0.5 }}>
                  ⚠️ Danger Zone
                </Typography>
                <Typography sx={{ color: '#888', fontSize: '0.82rem', mb: 2 }}>
                  These actions affect the entire system. Proceed with caution.
                </Typography>
                <Stack direction="row" spacing={2}>
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={clearingCache ? <CircularProgress size={18} color="error" /> : <Icon icon="mdi:database-refresh-outline" />}
                    disabled={clearingCache}
                    onClick={() => {
                      setClearingCache(true);
                      clearRedisCache((err, result) => {
                        setClearingCache(false);
                        if (!err) {
                          setSnackbar({ open: true, message: 'Redis cache cleared successfully!', severity: 'success' });
                        } else {
                          setSnackbar({ open: true, message: 'Failed to clear cache', severity: 'error' });
                        }
                      });
                    }}
                    sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 2 }}
                  >
                    {clearingCache ? 'Clearing...' : 'Clear Redis Cache'}
                  </Button>
                </Stack>
              </CardContent>
            </Card>

            {/* Save button */}
            <Box sx={{ display: 'flex', justifyContent: 'center', pb: 5 }}>
              <Button
                variant="contained"
                startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <Save />}
                onClick={handleSave}
                disabled={saving}
                size="large"
                sx={{
                  background: 'linear-gradient(45deg, #00d4ff, #7b2ff7)',
                  fontWeight: 'bold',
                  px: 6,
                  py: 2,
                  '&:hover': { background: 'linear-gradient(45deg, #00b8d4, #6a1bd4)' },
                }}
              >
                {saving ? 'Saving...' : 'SAVE ALL CHANGES'}
              </Button>
            </Box>
          </Stack>
        )}

        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    </Page>
  );
}
